import { Injectable } from '@angular/core';
import { HttpClient, HttpResponse } from '@angular/common/http';
import { environment } from '../../../environments/environment';
import { Observable } from 'rxjs';
import { ADHERENT_DATE_FIELDS_DEFINITION, IAdherent } from '../model/adherent.model';
import {
  ADHERENT_INFORMATIONS_DATE_FIELDS_DEFINITION,
  IAdherentInformations
} from '../model/adherent-informations.model';
import {CampAdherentType, TypeAdherentEnum, TypeCampGroupEnum, TypeDossierEnum} from '../constant/enum';
import { map } from 'rxjs/operators';
import { convertDateFromServer } from '../util/moment.utils';
import { IStructureAdherent } from '../model/structure-adherent.model';
import {IMailOptions} from "../model/mail-options.model";
import {IDdcFunctionality} from "../model/ddc-user.model";
import {AUTH_STORAGE_KEY} from "../constant/constant";
import {LocalCacheService} from "./local-cache.service";

@Injectable({
  providedIn: 'root'
})
export class AdherentService {

  constructor(protected http: HttpClient) {
  }

  public readonly resourceUrl = environment.apiServerUrl + 'adherents';

  static convertAhderentInformationsDateFromServer(adherentInformations: IAdherentInformations): IAdherentInformations {
    if (adherentInformations) {
      convertDateFromServer(adherentInformations, ADHERENT_INFORMATIONS_DATE_FIELDS_DEFINITION);
    }
    return adherentInformations;
  }

  static convertAhderentDateFromServer(adherent: IAdherent): IAdherent {
    if (adherent) {
      convertDateFromServer(adherent, ADHERENT_DATE_FIELDS_DEFINITION);
    }
    return adherent;
  }

  find(): Observable<HttpResponse<IAdherent[]>> {
    return this.http
      .get<IAdherent[]>(this.resourceUrl, {observe: 'response'});
  }

  findByNumero(codeAdherent: string, adherentType?: string, typeCampGroupEnum?: TypeCampGroupEnum): Observable<HttpResponse<IAdherent>> {
    if(adherentType === TypeAdherentEnum.STAFF || typeCampGroupEnum === TypeCampGroupEnum.COMPAGNON){
      return this.http
        .get<IAdherent>(`${this.resourceUrl}/${codeAdherent}`, {observe: 'response'})
        .pipe(map(res => this.convertAdherentDateFromServer(res)));
    }
    const query = adherentType ? `?adherentType=${adherentType}` : '';
      return this.http
        .get<IAdherent>(`${this.resourceUrl}/${codeAdherent}${query}`, {observe: 'response'})
        .pipe(map(res => this.convertAdherentDateFromServer(res)));
  }

  findByCodeStructure(codeStructure: string, campAdherentType: CampAdherentType, structureDependanteBool: boolean): Observable<HttpResponse<IAdherent[]>> {
    return this.http
      .get<IAdherent[]>(`${this.resourceUrl}?codeStructure=${codeStructure}&campAdherentType=${campAdherentType}&structureDependante=${structureDependanteBool}`, {observe: 'response'})
      .pipe(map(res => this.convertAdherentDateArrayFromServer(res)));
  }

  getAdherentInformations(numero: string, typeDossier: TypeDossierEnum, idCamp?: number): Observable<HttpResponse<IAdherentInformations>> {
    const params = idCamp ? `typeDossier=${typeDossier}&idCamp=${idCamp}` : `typeDossier=${typeDossier}`;
    return this.http
      .get<IAdherentInformations>(`${this.resourceUrl}/informations/${numero}?${params}`, {observe: 'response'})
      .pipe(map(res => this.convertAdherentInformationsDateFromServer(res)));
  }

  getAdherentsInformations(codesAdherent: string[], typeDossier: TypeDossierEnum, idCamp?: number): Observable<HttpResponse<IAdherentInformations[]>> {
    return this.http
      .post<IAdherentInformations[]>(`${this.resourceUrl}/informations`, {codesAdherent, typeDossier, idCamp}, {observe: 'response'})
      .pipe(map(res => this.convertAdherentInformationsDateArrayFromServer(res)));
  }

  getAdherentFonctions(codeAdherent: string[]): Observable<HttpResponse<IStructureAdherent>> {
    return this.http
      .get<IStructureAdherent>(`${this.resourceUrl}/fonctions/${codeAdherent}`, {observe: 'response'})
  }

  /**
   * Convertit les dates renvoyées par le serveur en date cliente (pour les informations d'adherent)
   * @param response
   */
  protected convertAdherentInformationsDateFromServer(response: HttpResponse<IAdherentInformations>): HttpResponse<IAdherentInformations> {
    return new HttpResponse({
      ...response,
      body: AdherentService.convertAhderentInformationsDateFromServer(response.body)
    });
  }

  /**
   * Convertit les dates renvoyées par le serveur en date cliente (pour les informations des adherents)
   * @param response
   */
  protected convertAdherentInformationsDateArrayFromServer(response: HttpResponse<IAdherentInformations[]>): HttpResponse<IAdherentInformations[]> {
    if (response.body) {
      response.body.forEach((adherent: IAdherent) => {
        AdherentService.convertAhderentInformationsDateFromServer(adherent);
      });
    }
    return response;

  }

  /**
   * Convertit les dates renvoyées par le serveur en date cliente
   * @param response
   */
  protected convertAdherentDateFromServer(response: HttpResponse<IAdherent>): HttpResponse<IAdherent> {
    return new HttpResponse({
      ...response,
      body: AdherentService.convertAhderentDateFromServer(response.body)
    });
  }

  /**
   * Convertit les dates renvoyées par le serveur en date cliente (pour plusieurs adherent)
   * @param res
   */
  protected convertAdherentDateArrayFromServer(res: HttpResponse<IAdherent[]>): HttpResponse<IAdherent[]> {
    if (res.body) {
      res.body.forEach((adherent: IAdherent) => {
        AdherentService.convertAhderentDateFromServer(adherent);
      });
    }
    return res;
  }

  updateCampOrProjetAnneeMembersData(typeDdc: TypeDossierEnum, idDdc: number): Observable<HttpResponse<IAdherent[]>> {
    return this.http
      .get<IAdherent[]>(`${this.resourceUrl}/update/update-camp-or-projet-annee-adherents-data?typeDdc=${typeDdc}&idDdc=${idDdc}`, {observe: 'response'})
  }
}
